import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import isToday from 'dayjs/plugin/isToday';
import type { TFunction } from 'i18next';

dayjs.extend(isToday);
dayjs.extend(isSameOrBefore);
dayjs.extend(duration);

const ORDERED_UNITS = [
  'year',
  'month',
  'day',
  'hour',
  'minute',
  'second',
  'millisecond',
] as const;
type OrderedUnit = (typeof ORDERED_UNITS)[number];

const msToDuration = (
  startMs: number,
  endMs: number,
): Record<OrderedUnit, number> => {
  const from = new Date(startMs);
  const to = new Date(endMs);

  let year = to.getUTCFullYear() - from.getUTCFullYear();
  let month = to.getUTCMonth() - from.getUTCMonth();
  let day = to.getUTCDate() - from.getUTCDate();
  let hour = to.getUTCHours() - from.getUTCHours();
  let minute = to.getUTCMinutes() - from.getUTCMinutes();
  let second = to.getUTCSeconds() - from.getUTCSeconds();
  let millisecond = to.getUTCMilliseconds() - from.getUTCMilliseconds();

  if (millisecond < 0) {
    millisecond += 1000;
    second -= 1;
  }
  if (second < 0) {
    second += 60;
    minute -= 1;
  }
  if (minute < 0) {
    minute += 60;
    hour -= 1;
  }
  if (hour < 0) {
    hour += 24;
    day -= 1;
  }
  if (day < 0) {
    day += new Date(
      Date.UTC(from.getUTCFullYear(), from.getUTCMonth() + month + 1, 0),
    ).getUTCDate();
    month -= 1;
  }
  if (month < 0) {
    month += 12;
    year -= 1;
  }

  return { year, month, day, hour, minute, second, millisecond };
};
const formatDuration = (
  startMs: number,
  endMs: number,
  t: TFunction,
  precision: OrderedUnit = 'second',
): string => {
  const units = msToDuration(startMs, endMs);
  const cutoff = ORDERED_UNITS.indexOf(precision);
  const formatted = ORDERED_UNITS.slice(0, cutoff + 1)
    .filter((unit) => units[unit] > 0)
    .map(
      (unit) => `${units[unit]}${t(`datetime.${unit}`, { ns: 'translation' })}`,
    )
    .join(' ');

  if (formatted) return formatted;
  if (endMs > startMs && precision === 'minute') {
    return `<1${t('datetime.minute', { ns: 'translation' })}`;
  }
  return '';
};

export const getFormattedDurationFromRange = (
  startDate: string,
  endDate: string,
  t: TFunction,
  precision?: (typeof ORDERED_UNITS)[number],
): string => {
  if (!startDate) return '';
  const startMs = Date.parse(startDate);
  const endMs = endDate ? Date.parse(endDate) : Date.now();
  return formatDuration(startMs, endMs, t, precision);
};

export const getFormattedDurationFromValue = (
  value: number | null | undefined,
  t: TFunction,
  precision?: OrderedUnit,
  unit: OrderedUnit = 'millisecond',
): string => {
  if (value == null || !Number.isFinite(value)) return '';
  const ms = dayjs.duration(value, unit).asMilliseconds();
  if (ms <= 0) return '';
  return formatDuration(0, ms, t, precision);
};